/**
 * One Heart shop / wallet UI.
 *
 *  - HUD coin display (top-right) shows current balance.
 *  - Press B to open the modal: grid of shop items, "Buy" or "Equip" / "Place".
 *  - Notifies a listener when wallet/inventory/equipped changes so MainScene
 *    can update visual layers (drink emoji, hat emoji, halo color).
 */

export type Slot = "hat" | "halo" | "drink" | "carry" | "fx" | "body" | "eyes" | "hair" | "outfit" | "headgear" | "accessory" | "premade";

export interface ShopItem {
  id: string;
  name: string;
  category: "appearance" | "drink" | "furniture" | "powerup";
  slot?: Slot;
  price: number;
  description: string;
  icon: string;
  effect?: { haloHex?: string; haloRadius?: number; moodSlowdown?: number; presenceBoost?: number };
  furnitureSlot?: number;
  emoji?: string;
  // Phase 12.4 — LimeZu sprite-driven items
  thumb?: string;
  asset_layer?: string;
  asset_family?: number;
  asset_variant?: number;
  asset_path?: string;
  shop?: string;
}

export interface WalletState {
  coins: number;
  coins_earned_lifetime: number;
  minutes_online_lifetime: number;
}

export interface ShopState {
  wallet: WalletState;
  inventory: Array<{ item_id: string; qty: number; acquired_at: number }>;
  equipped: Record<string, string>;
  placed: Array<{ item_id: string; pos_index: number }>;
}

export type ShopChangeListener = (state: ShopState) => void;

const API_BASE = (() => {
  if (location.port === "5173") return `${location.protocol}//${location.hostname}:2567`;
  return "";
})();

export class Shop {
  private token: string;
  private catalog: ShopItem[] = [];
  private state: ShopState | null = null;
  private listeners = new Set<ShopChangeListener>();
  private hudEl: HTMLDivElement | null = null;
  private modalEl: HTMLDivElement | null = null;
  private isOpen = false;

  constructor(token: string) {
    this.token = token;
  }

  async start() {
    await Promise.all([this.fetchCatalog(), this.refresh()]);
    this.buildHud();
    this.buildModal();
    // Re-fetch wallet every 30 seconds so coin earnings show up automatically.
    window.setInterval(() => this.refresh(), 30_000);
  }

  onChange(fn: ShopChangeListener) {
    this.listeners.add(fn);
    if (this.state) fn(this.state);
    return () => this.listeners.delete(fn);
  }

  toggleModal() {
    this.isOpen ? this.closeModal() : this.openModal();
  }

  isModalOpen() {
    return this.isOpen;
  }

  getState() {
    return this.state;
  }

  getCatalog() {
    return this.catalog;
  }

  findItem(id: string) {
    return this.catalog.find((c) => c.id === id);
  }

  // ────────────────── data fetches ──────────────────

  private async fetchCatalog() {
    const r = await fetch(`${API_BASE}/api/shop/catalog`);
    if (!r.ok) throw new Error("catalog fetch failed");
    const data = await r.json();
    this.catalog = data.items || [];
  }

  private async refresh() {
    const r = await fetch(`${API_BASE}/api/me/wallet`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!r.ok) return;
    this.state = await r.json();
    this.updateHud();
    if (this.isOpen) this.renderModal();
    if (this.state) for (const fn of this.listeners) fn(this.state);
  }

  // ────────────────── HUD ──────────────────

  private buildHud() {
    const div = document.createElement("div");
    div.id = "coin-hud";
    div.style.cssText = `
      position: fixed; top: 14px; right: 14px;
      background: linear-gradient(135deg, #fde047 0%, #f59e0b 100%);
      color: #422006;
      font-family: ui-sans-serif, system-ui;
      font-weight: 700;
      font-size: 14px;
      padding: 8px 14px;
      border-radius: 999px;
      box-shadow: 0 0 18px rgba(245, 158, 11, 0.6);
      cursor: pointer;
      user-select: none;
      z-index: 1000;
      letter-spacing: 0.04em;
    `;
    div.title = "Click to open shop (or press B)";
    div.addEventListener("click", () => this.toggleModal());
    document.body.appendChild(div);
    this.hudEl = div;
    this.updateHud();
  }

  private updateHud() {
    if (!this.hudEl) return;
    const c = this.state?.wallet?.coins ?? 0;
    this.hudEl.textContent = `🪙 ${c.toLocaleString()}`;
  }

  // ────────────────── Modal ──────────────────

  private buildModal() {
    const div = document.createElement("div");
    div.id = "shop-modal";
    div.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(10, 5, 20, 0.85);
      backdrop-filter: blur(6px);
      z-index: 2000;
      display: none;
      align-items: center; justify-content: center;
      padding: 24px;
    `;
    div.addEventListener("click", (e) => {
      if (e.target === div) this.closeModal();
    });
    document.body.appendChild(div);
    this.modalEl = div;
  }

  openModal() {
    if (!this.modalEl) return;
    this.isOpen = true;
    this.modalEl.style.display = "flex";
    this.renderModal();
    this.refresh();
  }

  closeModal() {
    if (!this.modalEl) return;
    this.isOpen = false;
    this.modalEl.style.display = "none";
  }

  private renderModal() {
    if (!this.modalEl || !this.state) return;
    while (this.modalEl.firstChild) this.modalEl.removeChild(this.modalEl.firstChild);

    const panel = document.createElement("div");
    panel.style.cssText = `
      background: linear-gradient(180deg, #1a1430 0%, #2d1b4e 100%);
      border: 1px solid #ff2e88;
      box-shadow: 0 0 60px rgba(255, 46, 136, 0.4);
      border-radius: 16px;
      padding: 24px;
      width: min(900px, 95vw);
      max-height: 90vh;
      overflow-y: auto;
      color: #f3e8ff;
      font-family: ui-sans-serif, system-ui;
    `;

    const header = document.createElement("div");
    header.style.cssText = "display:flex; align-items:center; justify-content:space-between; margin-bottom:16px;";
    const title = document.createElement("h1");
    title.textContent = "♥ ONE HEART · SHOP";
    title.style.cssText = "color:#ff2e88; margin:0; font-size:22px; letter-spacing:0.08em;";
    const balance = document.createElement("div");
    balance.style.cssText = "background:#fde047; color:#422006; padding:6px 12px; border-radius:999px; font-weight:700;";
    balance.textContent = `🪙 ${this.state.wallet.coins.toLocaleString()}`;
    header.appendChild(title);
    header.appendChild(balance);
    panel.appendChild(header);

    const sub = document.createElement("p");
    sub.style.cssText = "color:#c4b5fd; font-size:11px; margin:0 0 16px; letter-spacing:0.04em;";
    sub.textContent = `Earn 1 coin per minute online in Claude. ${this.state.wallet.minutes_online_lifetime} min lifetime · ${this.state.wallet.coins_earned_lifetime} earned · Press B or Esc to close.`;
    panel.appendChild(sub);

    const ownedSet = new Set(this.state.inventory.map((i) => i.item_id));
    const equippedSet = new Set(Object.values(this.state.equipped));
    const placedSet = new Set(this.state.placed.map((p) => p.item_id));

    // Group by shop, then by category. LimeZu items have a `shop` field;
    // legacy items don't, so they go under "Classic" sections per category.
    const SHOP_LABELS: Record<string, string> = {
      hat_shop: "Hat Shop",
      boutique: "Boutique (Outfits)",
      hair_salon: "Hair Salon",
      optical: "Optical Center (Eye Color)",
      body_mod: "Body Mod Studio (Skin Tone)",
      accessory: "Accessory Stand",
      tech_bazaar: "Tech Bazaar (Phones / Books)",
      sword_stand: "Sword Stand",
      costume_hall: "Costume Hall (Premade Looks)",
    };
    const SHOP_ORDER = [
      "hat_shop", "boutique", "hair_salon", "optical", "body_mod",
      "accessory", "tech_bazaar", "costume_hall", "sword_stand",
    ];

    // Render LimeZu shops first (they're the bulk of the catalog).
    for (const shopKey of SHOP_ORDER) {
      const items = this.catalog.filter((c) => c.shop === shopKey);
      if (items.length === 0) continue;
      const h = document.createElement("h2");
      h.textContent = `${SHOP_LABELS[shopKey] || shopKey} · ${items.length} items`;
      h.style.cssText = "color:#fde047; font-size:13px; margin:18px 0 8px; letter-spacing:0.06em; text-transform:uppercase;";
      panel.appendChild(h);
      const grid = document.createElement("div");
      grid.style.cssText = "display:grid; grid-template-columns:repeat(auto-fill, minmax(140px, 1fr)); gap:8px;";
      for (const item of items) {
        grid.appendChild(this.renderItemCard(item, ownedSet, equippedSet, placedSet));
      }
      panel.appendChild(grid);
    }

    // Halos — pure-graphic ring around the avatar, the only legacy category
    // retained after Phase 12 cleanup.
    const halos = this.catalog.filter((c) => c.slot === "halo");
    if (halos.length > 0) {
      const h = document.createElement("h2");
      h.textContent = `Halo Booth · ${halos.length} items`;
      h.style.cssText = "color:#fde047; font-size:13px; margin:18px 0 8px; letter-spacing:0.06em; text-transform:uppercase;";
      panel.appendChild(h);
      const grid = document.createElement("div");
      grid.style.cssText = "display:grid; grid-template-columns:repeat(auto-fill, minmax(140px, 1fr)); gap:8px;";
      for (const item of halos) {
        grid.appendChild(this.renderItemCard(item, ownedSet, equippedSet, placedSet));
      }
      panel.appendChild(grid);
    }

    this.modalEl.appendChild(panel);
  }

  private renderItemCard(item: ShopItem, ownedSet: Set<string>, equippedSet: Set<string>, placedSet: Set<string>): HTMLDivElement {
    const owned = ownedSet.has(item.id) || item.price === 0;
    const equipped = equippedSet.has(item.id);
    const placed = placedSet.has(item.id);
    const canAfford = (this.state?.wallet.coins ?? 0) >= item.price;

    const card = document.createElement("div");
    card.style.cssText = `
      background: rgba(76, 29, 149, 0.35);
      border: 1px solid ${equipped || placed ? "#fde047" : owned ? "#a78bfa" : canAfford ? "#5b21b6" : "#3f1d6b"};
      border-radius: 10px;
      padding: 12px;
      display: flex; flex-direction: column;
      gap: 6px;
      ${equipped || placed ? "box-shadow: 0 0 12px rgba(253, 224, 71, 0.4);" : ""}
    `;

    const top = document.createElement("div");
    top.style.cssText = "display:flex; align-items:center; gap:8px;";
    if (item.thumb) {
      const img = document.createElement("img");
      img.src = item.thumb;
      img.style.cssText = "width:40px; height:54px; object-fit:contain; image-rendering:pixelated; flex-shrink:0;";
      img.onerror = () => { img.style.display = "none"; };
      top.appendChild(img);
    } else {
      const ico = document.createElement("span");
      ico.style.cssText = "font-size:22px;";
      ico.textContent = item.icon;
      top.appendChild(ico);
    }
    const name = document.createElement("strong");
    name.textContent = item.name;
    name.style.cssText = "font-size:12px;";
    top.appendChild(name);
    card.appendChild(top);

    const desc = document.createElement("div");
    desc.style.cssText = "color:#c4b5fd; font-size:11px; min-height:28px;";
    desc.textContent = item.description;
    card.appendChild(desc);

    const price = document.createElement("div");
    price.style.cssText = "font-size:11px; color:#fde047; font-weight:700;";
    price.textContent = item.price === 0 ? "Free" : `🪙 ${item.price}`;
    card.appendChild(price);

    const btn = document.createElement("button");
    btn.style.cssText = `
      margin-top:4px;
      border:none; padding:8px;
      border-radius:6px;
      font-weight:700;
      font-size:12px;
      cursor: pointer;
      letter-spacing: 0.04em;
    `;

    if (equipped) {
      btn.textContent = "EQUIPPED — UNEQUIP";
      btn.style.background = "#fde047";
      btn.style.color = "#422006";
      btn.onclick = () => this.act("unequip", { slot: item.slot });
    } else if (placed) {
      btn.textContent = "PLACED — REMOVE";
      btn.style.background = "#fde047";
      btn.style.color = "#422006";
      btn.onclick = () => this.act("unplace", { itemId: item.id });
    } else if (owned) {
      if (item.slot) {
        btn.textContent = "EQUIP";
        btn.style.background = "#a78bfa";
        btn.style.color = "#1a1430";
        btn.onclick = () => this.act("equip", { itemId: item.id });
      } else if (item.category === "furniture") {
        btn.textContent = "PLACE IN ROOM";
        btn.style.background = "#a78bfa";
        btn.style.color = "#1a1430";
        btn.onclick = () => this.act("place", { itemId: item.id });
      } else {
        btn.textContent = "OWNED";
        btn.style.background = "#5b21b6";
        btn.style.color = "#c4b5fd";
        btn.disabled = true;
      }
    } else {
      btn.textContent = canAfford ? `BUY — 🪙 ${item.price}` : `🔒 ${item.price - (this.state?.wallet.coins ?? 0)} more coins`;
      btn.style.background = canAfford ? "#ff2e88" : "#3f1d6b";
      btn.style.color = canAfford ? "white" : "#6b7280";
      btn.disabled = !canAfford;
      if (canAfford) btn.onclick = () => this.act("buy", { itemId: item.id });
    }
    card.appendChild(btn);
    return card;
  }

  private async act(kind: "buy" | "equip" | "unequip" | "place" | "unplace", payload: any) {
    const map: Record<string, string> = {
      buy: "/api/shop/buy",
      equip: "/api/me/equip",
      unequip: "/api/me/unequip",
      place: "/api/me/place",
      unplace: "/api/me/unplace",
    };
    try {
      const r = await fetch(`${API_BASE}${map[kind]}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: "?" }));
        console.warn(`[shop] ${kind} failed:`, err);
        return;
      }
      await this.refresh();
    } catch (e) {
      console.error(`[shop] ${kind} error:`, e);
    }
  }
}
